import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Law } from '../../Entities/Law';

const Sidebar = ({ isMobileOpen = false, setIsMobileOpen = () => {} }) => {
  const [expandedChapter, setExpandedChapter] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [lawData, setLawData] = useState(null);

  // Get current law type from the route
  const getCurrentLawType = () => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('id');
    if (idParam) return idParam;
    if (location.pathname.includes('/dpdp-rules')) return 'dpdp-rules';
    if (location.pathname.includes('/dpdp')) return 'dpdp';
    return 'dpdp-rules'; // default fallback
  };

  const currentLawType = getCurrentLawType();

  useEffect(() => {
    try {
      const data = Law.list();
      const currentLaw = data.find(law => law.id === currentLawType) || data[0];
      setLawData(currentLaw);
    } catch (error) {
      // Silently handle law data loading errors
    }
  }, [currentLawType]);

  // Get current article from URL
  const getCurrentArticle = () => {
    const params = new URLSearchParams(location.search);
    return parseInt(params.get('article')) || null;
  };

  const getCurrentSchedule = () => {
    const params = new URLSearchParams(location.search);
    return parseInt(params.get('schedule')) || null;
  };

  const getCurrentPart = () => {
    const params = new URLSearchParams(location.search);
    return params.get('part') || null;
  };

  const currentArticle = getCurrentArticle();
  const currentSchedule = getCurrentSchedule();
  const currentPart = getCurrentPart();

  // Expand the chapter containing the current article
  useEffect(() => {
    if (currentArticle && lawData) {
      const chapter = lawData.chapters.find(ch => 
        ch.articles.some(art => art.number === currentArticle)
      );
      if (chapter) {
        setExpandedChapter(chapter.number);
      }
    }
  }, [currentArticle, lawData]);

  // Auto-expand schedule dropdown when navigating to a schedule with parts
  useEffect(() => {
    if (currentSchedule && lawData) {
      const schedule = lawData.schedules?.find(s => s.number === currentSchedule);
      if (schedule && schedule.parts) {
        setExpandedChapter(`schedule-${schedule.number}`);
      }
    }
  }, [currentSchedule, currentPart, lawData]);

  // Scroll active sidebar item into view
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    // Small delay to allow dropdown expansion to render
    const timer = setTimeout(() => {
      if (!scrollContainerRef.current) return;
      const activeEl = scrollContainerRef.current.querySelector('[data-sidebar-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [currentArticle, currentSchedule, currentPart, expandedChapter]);

  const handleChapterToggle = (chapterNumber) => {
    setExpandedChapter(expandedChapter === chapterNumber ? null : chapterNumber);
  };

  const handleArticleClick = (chapterNumber, articleNumber) => {
    navigate(`/law-detail?id=${currentLawType}&chapter=${chapterNumber}&article=${articleNumber}`);
  };

  const handleScheduleClick = (scheduleNumber, part) => {
    let url = `/law-detail?id=${currentLawType}&schedule=${scheduleNumber}`;
    if (part) url += `&part=${part}`;
    navigate(url);
  };

  if (!lawData) {
    return (
      <div className="w-80 bg-white border-r border-blue-200 p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-blue-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 bg-blue-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[44] lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        w-[85vw] max-w-96
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed top-14 sm:top-16 left-0 bottom-0 z-[45]
        bg-white border-r border-blue-200 transition-transform duration-300 ease-in-out flex-shrink-0
        overflow-hidden flex flex-col
        pl-2 sm:pl-4
      `}>
        {/* Sidebar Header */}
        <div className="border-b border-blue-200 bg-white flex-shrink-0">
          <div className="flex items-center justify-between p-3">
            {
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-bold text-black">
                  {lawData ? lawData.title : 'Loading...'}
                </h2>
                <p className="text-[0.65rem] text-blue-700 mt-1">
                  {lawData ? lawData.short_title : ''}
                </p>
              </div>
            }
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
          <div className="p-3 sm:p-4 space-y-0">
            {lawData.chapters.map((chapter) => (
              <div key={chapter.number} className="mb-0">
                <button
                  onClick={() => {
                    handleChapterToggle(chapter.number);
                    if (isMobileOpen) setIsMobileOpen(false);
                  }}
                  className={`
                    w-full flex items-center justify-between px-3 sm:px-4 py-2 text-left transition-all duration-200 border-b
                    ${expandedChapter === chapter.number 
                      ? 'text-black font-semibold bg-blue-50 border-b-2 border-blue-500' 
                      : 'text-blue-700 hover:text-black border-b border-blue-200'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.7rem] text-blue-600 mb-0.5">
                      Chapter {chapter.number}
                    </div>
                    <div className="font-medium text-xs sm:text-sm leading-tight">
                      {chapter.name}
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 transition-transform ${
                      expandedChapter === chapter.number ? 'rotate-90' : ''
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Articles List */}
                {expandedChapter === chapter.number && (
                  <div className="mt-0 ml-0 space-y-0">
                    {chapter.articles.map((article) => (
                      <button
                        key={article.number}
                        data-sidebar-active={currentArticle === article.number ? 'true' : undefined}
                        onClick={() => {
                          handleArticleClick(chapter.number, article.number);
                          if (isMobileOpen) setIsMobileOpen(false);
                        }}
                        className={`
                          w-full text-left px-3 sm:px-4 py-2 text-[0.75rem] sm:text-xs transition-all duration-200 border-b
                          ${currentArticle === article.number
                            ? 'text-black font-semibold bg-blue-50 border-b-2 border-blue-500'
                            : 'text-blue-700 hover:text-black bg-white border-b border-blue-200 hover:bg-blue-50'
                          }
                        `}
                      >
                        <div className="flex items-start space-x-1">
                          <span className="font-medium flex-shrink-0">{currentLawType === 'dpdp-rules' ? 'Rule' : 'Sec.'} {article.number}</span>
                          <span className="text-xs opacity-50 flex-shrink-0">–</span>
                          <span className="flex-1 text-justify-clean">{article.title}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Schedules */}
            {lawData.schedules && lawData.schedules.length > 0 && (
              <>
                <div className="pt-3 pb-1 px-3">
                  <div className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-wider">Schedules</div>
                </div>
                {lawData.schedules.map((schedule) => (
                  <div key={schedule.number} className="mb-0">
                    {schedule.parts ? (
                      <>
                        <button
                          onClick={() => {
                            handleChapterToggle(`schedule-${schedule.number}`);
                            if (isMobileOpen) setIsMobileOpen(false);
                          }}
                          className={`
                            w-full flex items-center justify-between px-3 sm:px-4 py-2 text-left transition-all duration-200 border-b
                            ${expandedChapter === `schedule-${schedule.number}`
                              ? 'text-black font-semibold bg-blue-50 border-b-2 border-blue-500' 
                              : 'text-blue-700 hover:text-black border-b border-blue-200'
                            }
                          `}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs sm:text-sm leading-tight">
                              {schedule.name}
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 transition-transform ${
                              expandedChapter === `schedule-${schedule.number}` ? 'rotate-90' : ''
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {expandedChapter === `schedule-${schedule.number}` && (
                          <div className="mt-0 ml-0 space-y-0">
                            {schedule.parts.map((part) => (
                              <button
                                key={part.part}
                                data-sidebar-active={currentSchedule === schedule.number && currentPart === part.part ? 'true' : undefined}
                                onClick={() => {
                                  handleScheduleClick(schedule.number, part.part);
                                  if (isMobileOpen) setIsMobileOpen(false);
                                }}
                                className={`
                                  w-full text-left px-3 sm:px-4 py-2 text-[0.75rem] sm:text-xs transition-all duration-200 border-b
                                  ${currentSchedule === schedule.number && currentPart === part.part
                                    ? 'text-black font-semibold bg-blue-50 border-b-2 border-blue-500'
                                    : 'text-blue-700 hover:text-black bg-white border-b border-blue-200 hover:bg-blue-50'
                                  }
                                `}
                              >
                                <div className="flex items-start space-x-1">
                                  <span className="font-medium flex-shrink-0">Part {part.part}</span>
                                  <span className="text-xs opacity-50 flex-shrink-0">–</span>
                                  <span className="flex-1 text-justify-clean">{part.title}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        data-sidebar-active={currentSchedule === schedule.number && !currentPart ? 'true' : undefined}
                        onClick={() => {
                          handleScheduleClick(schedule.number);
                          if (isMobileOpen) setIsMobileOpen(false);
                        }}
                        className={`
                          w-full flex items-center justify-between px-3 sm:px-4 py-2 text-left transition-all duration-200 border-b
                          ${currentSchedule === schedule.number
                            ? 'text-black font-semibold bg-blue-50 border-b-2 border-blue-500' 
                            : 'text-blue-700 hover:text-black border-b border-blue-200'
                          }
                        `}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-xs sm:text-sm leading-tight">
                            {schedule.name}
                          </div>
                        </div>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
            <div className="pb-8"></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;